type LightboxItem = {
  targetSelector: string;
  trigger: HTMLElement;
  content: HTMLElement;
};

class Lightbox {
  private overlay: HTMLDivElement;
  private inner: HTMLDivElement;
  private btnPrev: HTMLButtonElement;
  private btnNext: HTMLButtonElement;
  private btnClose: HTMLButtonElement;

  private items: LightboxItem[] = [];
  private currentIndex = 0;

  constructor(triggerSelector: string) {
    this.overlay = this.createOverlay();
    this.inner = this.overlay.querySelector(
      '[data-role="lb-inner"]'
    ) as HTMLDivElement;
    this.btnPrev = this.overlay.querySelector(
      '[data-role="lb-prev"]'
    ) as HTMLButtonElement;
    this.btnNext = this.overlay.querySelector(
      '[data-role="lb-next"]'
    ) as HTMLButtonElement;
    this.btnClose = this.overlay.querySelector(
      '[data-role="lb-close"]'
    ) as HTMLButtonElement;

    document.body.appendChild(this.overlay);

    this.bindTriggers(triggerSelector);
    this.bindEvents();
  }

  private createOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className =
      'lightbox-overlay fixed inset-0 bg-black/60 hidden z-[9999] flex items-center justify-center p-6';

    overlay.innerHTML = `
      <div class="flex justify-center md:gap-20 gap-5 items-center w-full">
        <button data-role="lb-prev" class="!relative -translate-y-1/2  bg-white border-[2px] border-[#906C25] rounded-[50%] after:!text-sm !w-[44px] after:!text-[#906C25] [&.gastronomic-swiper-disabled]:border-[#E0D3AD] [&.gastronomic-swiper-disabled]:after:!text-[#E0D3AD] [&.gastronomic-swiper-disabled]:cursor-default swiper-button-prev "></button>
        <div data-role="lb-inner" class="bg-white rounded shadow-lg max-w-3xl w-full max-h-[90vh] overflow-auto p-6 relative flex-1">
          <button data-role="lb-close" class="fixed text-white top-10 right-10 text-5xl">&times;</button>
        </div>
        <button data-role="lb-next" class="!relative -translate-y-1/2  bg-white border-[2px] border-[#906C25] rounded-[50%] after:!text-sm !w-[44px] after:!text-[#906C25] [&.gastronomic-swiper-disabled]:border-[#E0D3AD] [&.gastronomic-swiper-disabled]:after:!text-[#E0D3AD] [&.gastronomic-swiper-disabled]:cursor-default swiper-button-next "></button>
      </div>
    `;
    return overlay;
  }

  private bindTriggers(selector: string) {
    const triggers = document.querySelectorAll<HTMLElement>(selector);

    triggers.forEach((trigger) => {
      const targetSelector = trigger.dataset.lbTarget;
      if (!targetSelector) return;

      const content = document.querySelector(targetSelector) as HTMLElement;
      if (!content) return;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const group = trigger.dataset.lbGroup || 'default';

        this.items = this.collectGroupItems(group);
        this.currentIndex = this.items.findIndex(
          (item) => item.trigger === trigger
        );

        this.open();
      });
    });
  }

  private collectGroupItems(group: string): LightboxItem[] {
    const triggers = document.querySelectorAll<HTMLElement>(
      `[data-lb-group="${group}"]`
    );

    return Array.from(triggers).map((trigger) => {
      const targetSelector = trigger.dataset.lbTarget!;
      const content = document.querySelector(targetSelector) as HTMLElement;

      return {
        targetSelector,
        trigger,
        content,
      };
    });
  }

  private bindEvents() {
    this.btnPrev.addEventListener('click', () => this.showPrev());
    this.btnNext.addEventListener('click', () => this.showNext());
    this.btnClose.addEventListener('click', () => this.close());

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (this.overlay.classList.contains('hidden')) return;

      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowRight') this.showNext();
      if (e.key === 'ArrowLeft') this.showPrev();
    });
  }

  private open() {
    this.updateContent();
    this.overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  private close() {
    this.overlay.classList.add('hidden');
    document.body.style.overflow = '';
  }

  private updateContent() {
    const item = this.items[this.currentIndex];
    if (!item) return;

    // 既存内容を削除
    this.inner.querySelectorAll('.lb-dynamic').forEach((el) => el.remove());

    // 内容を複製して挿入
    const clone = item.content.cloneNode(true) as HTMLElement;
    clone.classList.add('lb-dynamic');
    clone.style.display = 'block';

    this.inner.appendChild(clone);
  }

  private showPrev() {
    this.currentIndex =
      (this.currentIndex - 1 + this.items.length) % this.items.length;
    this.updateContent();
  }

  private showNext() {
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.updateContent();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Lightbox('.js-lightbox');
});
